import React, { FC, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Line } from 'react-chartjs-2';
import api from '../../api';
import {
  COST_DIMENSIONS,
  CostDimension,
  CostDimensionFilters,
  CostFilterBar,
  DIMENSION_LABELS,
  dimensionFilterParams,
  hasEmptySelection,
} from './CostDimensionFilters';
import {
  formatLabel,
  formatUSD,
  OTHER_SERIES_COLOR,
  SERIES_COLORS,
} from './platformStatsChartUtils';

/**
 * "Daily Cost" stacked area chart on the Costs tab.
 *
 * Spend per day is broken down by one dimension (model display name,
 * trigger, or the agent type + node pair) and can be narrowed to any subset
 * of values on any dimension — e.g. the model breakdown for a single
 * trigger, or every model except two. Data comes from
 * `GET /platform-stats/daily-cost`, which shares the page's date range and
 * user filter via `baseQuery`.
 */

export type DailyCostDimension = CostDimension;
export type DailyCostFilters = CostDimensionFilters;
export { DIMENSION_LABELS, hasEmptySelection };

export interface DailyCostSeries {
  key: string;
  costs: number[];
  total: number;
}

export interface DailyCostResponse {
  success: boolean;
  days: string[];
  breakdown: DailyCostDimension;
  filters: DailyCostFilters;
  series: DailyCostSeries[];
  options: Record<DailyCostDimension, string[]>;
}

/** Series beyond this many are folded into a single "Other" series. */
export const MAX_VISIBLE_SERIES = 12;

/** Query string for the endpoint, given the page-level range/user fragment. */
export function dailyCostQuery(
  baseQuery: string,
  breakdown: DailyCostDimension,
  filters: DailyCostFilters,
): string {
  const params = [`breakdown=${breakdown}`, ...dimensionFilterParams(filters)];
  return `${baseQuery}&${params.join('&')}`;
}

/**
 * Keep the top series and fold the tail into "Other" so the legend stays
 * readable when the agent breakdown has dozens of type/node pairs.
 */
export function capSeries(
  series: DailyCostSeries[],
  dayCount: number,
  max = MAX_VISIBLE_SERIES,
): DailyCostSeries[] {
  if (series.length <= max) return series;
  const head = series.slice(0, max);
  const tail = series.slice(max);
  const other: DailyCostSeries = {
    key: `Other (${tail.length} more)`,
    costs: new Array(dayCount).fill(0),
    total: 0,
  };
  for (const s of tail) {
    other.total += s.total;
    s.costs.forEach((c, i) => {
      other.costs[i] += c;
    });
  }
  return [...head, other];
}

interface DailyCostChartProps {
  /** `startDate=…&endDate=…` or `days=N`, plus any `&filterUserId=…`. */
  baseQuery: string;
  /**
   * Breakdown/filter selection is owned by the parent so it survives the
   * Costs tab re-mounting while the page-level stats reload.
   */
  breakdown: DailyCostDimension;
  filters: DailyCostFilters;
  onBreakdownChange: (breakdown: DailyCostDimension) => void;
  onFiltersChange: (filters: DailyCostFilters) => void;
}

const DailyCostChart: FC<DailyCostChartProps> = ({
  baseQuery,
  breakdown,
  filters,
  onBreakdownChange,
  onFiltersChange,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<DailyCostResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const emptySelection = hasEmptySelection(filters);

  useEffect(() => {
    if (emptySelection) {
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<DailyCostResponse>(
          `/platform-stats/daily-cost?${dailyCostQuery(baseQuery, breakdown, filters)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setData(res.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load daily cost breakdown:', err);
        setError('Failed to load daily cost breakdown.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, baseQuery, breakdown, filters, emptySelection]);

  const options = data?.options;
  const days = data?.days ?? [];

  const visibleSeries = useMemo(
    () => (emptySelection ? [] : capSeries(data?.series ?? [], days.length)),
    [data, days.length, emptySelection],
  );

  const chartData = {
    labels: days.map(formatLabel),
    datasets: visibleSeries.map((s, i) => {
      const isOther = i >= MAX_VISIBLE_SERIES;
      const color = isOther
        ? OTHER_SERIES_COLOR
        : SERIES_COLORS[i % SERIES_COLORS.length];
      return {
        label: s.key,
        data: s.costs,
        backgroundColor: color,
        borderColor: color.replace(/0\.\d+\)$/, '1)'),
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        stack: 'cost',
      };
    }),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          boxWidth: 12,
          padding: 10,
        },
      },
      title: {
        display: true,
        text: `Daily Cost by ${DIMENSION_LABELS[data?.breakdown ?? breakdown]} ($)`,
        font: {
          family: 'Instrument Sans, sans-serif',
          size: 14,
          weight: 600 as const,
        },
        color: '#222',
        padding: { bottom: 12 },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${formatUSD(ctx.raw)}`,
          footer: (items: any[]) =>
            `Total: ${formatUSD(
              items.reduce((sum, item) => sum + Number(item.raw ?? 0), 0),
            )}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#f0f0f0' },
        ticks: {
          font: { family: 'Instrument Sans, sans-serif', size: 11 },
          color: '#888',
          callback: (v: any) => formatUSD(v),
        },
      },
    },
  };

  const hasSeries = visibleSeries.length > 0;

  return (
    <div className='costs-chart-card wide daily-cost-card'>
      <div className='daily-cost-controls'>
        <div
          className='daily-cost-breakdown'
          role='group'
          aria-label='Break down by'
        >
          <span className='control-label'>Break down by</span>
          {COST_DIMENSIONS.map((dim) => (
            <button
              key={dim}
              type='button'
              className={`breakdown-btn${breakdown === dim ? ' active' : ''}`}
              aria-pressed={breakdown === dim}
              onClick={() => onBreakdownChange(dim)}
            >
              {DIMENSION_LABELS[dim]}
            </button>
          ))}
        </div>
        <CostFilterBar
          dimensions={COST_DIMENSIONS}
          options={options}
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </div>
      <div className='daily-cost-chart'>
        {error ? (
          <div className='msg error'>{error}</div>
        ) : (data || emptySelection) && !hasSeries && !loading ? (
          <div className='loading'>No cost data for this selection.</div>
        ) : data ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <div className='loading'>Loading daily cost...</div>
        )}
      </div>
    </div>
  );
};

export default DailyCostChart;
